/**
 * WT1 - shrink a capture before it is queued: 2048 px on the long edge, JPEG 0.82 (his decision, 2026-09-20).
 *
 * ⭐ Why on the phone, before the queue: a 12 MP shot is ~4 MB; at 2048 px it is ~0.5-0.7 MB. A 40-photo tour
 *    is then ~25 MB instead of ~160 MB, which is the difference between draining on one bar and not.
 * ⚠️ The stated cost: 2048 px across an 11-inch page is ~186 DPI - right for the tour report, a PDF and the
 *    web, thin for a full-page print hero.
 *
 * ⛔⛔ A PHOTO IS NEVER LOST TO A CODEC. If the browser cannot decode it (an odd HEIC, a canvas that refuses,
 *    memory pressure on a long tour), the ORIGINAL file is returned untouched and queued as it is. A bigger
 *    upload is a cost; a capture that silently vanished is lost evidence from a building he may not get back
 *    into.
 */

export interface Size {
  width: number;
  height: number;
}

/** Scale (w, h) down so the long edge is at most `maxEdge`, keeping the aspect ratio. Never upscales. */
export const fitWithin = (width: number, height: number, maxEdge: number): Size => {
  if (!(width > 0) || !(height > 0) || !(maxEdge > 0)) return { width, height };
  const longEdge = Math.max(width, height);
  if (longEdge <= maxEdge) return { width, height };
  const scale = maxEdge / longEdge;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

interface Decoded {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

/**
 * `createImageBitmap` with `imageOrientation: 'from-image'` applies the EXIF rotation, so a portrait shot is
 * not drawn sideways. Older Safari lacks the options form (or the function), so the fallback is an <img>,
 * which modern browsers also draw EXIF-rotated (`image-orientation: from-image` is the CSS default).
 */
const decode = async (blob: Blob): Promise<Decoded> => {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
      return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() };
    } catch {
      // fall through to the <img> path
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.decoding = 'async';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('The photo could not be decoded'));
      img.src = url;
    });
    return {
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      release: () => URL.revokeObjectURL(url),
    };
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
};

/** Draw the decoded image into a canvas of the fitted size; null when the browser gives no 2D context. */
const drawFitted = (decoded: Decoded, maxEdge: number): HTMLCanvasElement | null => {
  const { width, height } = fitWithin(decoded.width, decoded.height, maxEdge);
  if (!(width > 0) || !(height > 0)) return null;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(decoded.source, 0, 0, width, height);
  return canvas;
};

const toJpegBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> =>
  new Promise((resolve) => {
    try {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
    } catch {
      resolve(null);
    }
  });

/**
 * The capture as it is queued: a JPEG no longer than `maxEdge` on its long side - or, when anything in the
 * decode/draw/encode chain fails, the ORIGINAL file (see the header).
 */
export const downscaleToJpeg = async (
  file: Blob,
  opts: { maxEdge?: number; quality?: number } = {},
): Promise<Blob> => {
  const maxEdge = opts.maxEdge ?? 2048;
  const quality = opts.quality ?? 0.82;
  let decoded: Decoded | null = null;
  try {
    decoded = await decode(file);
    const canvas = drawFitted(decoded, maxEdge);
    if (!canvas) return file;
    const jpeg = await toJpegBlob(canvas, quality);
    // An empty blob is a failed encode (seen on iOS under memory pressure), not a tiny photo.
    return jpeg && jpeg.size > 0 ? jpeg : file;
  } catch {
    return file;
  } finally {
    decoded?.release();
  }
};

/**
 * A small data URL for the grid, so the photos are visible OFFLINE after their full bytes are dropped
 * (an uploaded capture keeps only this). Null when it cannot be made - the grid shows a placeholder, and
 * the capture itself is unaffected.
 */
export const makeThumbnail = async (blob: Blob, maxEdge = 320): Promise<string | null> => {
  let decoded: Decoded | null = null;
  try {
    decoded = await decode(blob);
    const canvas = drawFitted(decoded, maxEdge);
    if (!canvas) return null;
    const url = canvas.toDataURL('image/jpeg', 0.7);
    return url.startsWith('data:image/') ? url : null;
  } catch {
    return null;
  } finally {
    decoded?.release();
  }
};
